<template>
  <div>
    <div class="layout layout--horizontal layout--horizontal-equal">
      <template v-if="!isPreAuth">
        <button
          v-if="paymentGateway.methods.roomCharge && !paymentGateway.methods.giftCard && !hiddenElements.roomCharge"
          class="button button--large button--primary"
          type="button"
          :disabled="disabled"
          @click="openModalHandler('chargeToRoom')"
        >
          {{ content.PaymentScreen.metadata.chargeRoomCta || 'Charge Room' }}
        </button>
      </template>

      <button
        class="button button--large button--primary"
        type="button"
        :disabled="disabled || isLoadingLibraries"
        @click="openModalHandler('paymentInformation')"
      >
        <template v-if="primaryCtaText">
          {{ primaryCtaText }}
        </template>

        <template v-else>
          {{ total === 0 ? 'Next' : content.OrderSummary.metadata.buttonCta }}
        </template>
      </button>
    </div>

    <modal name="paymentInformation">
      <!--
        the `session.isSignedIn` key forces the payment-information
        component to re-render when the value changes, so that the
        `mounted` or `created` methods in it's child components
        can re-run with the new signed in user data.
     -->
      <payment-information
        :key="session.isSignedIn"
        :force-save-cards="forceSaveCards"
        :pay-cta-text="payCtaText"
        :payment-gateway="paymentGateway"
        :payment-modal-title="paymentModalTitle"
        :payment-total-title="paymentTotalTitle"
        :phone-is-required="phoneIsRequired"
        :email-input-state="emailInputState"
        :is-pre-auth="isPreAuth"
        :room-number="roomNumber"
        :gift-card-reload-payment-method="giftCardReloadPaymentMethod"
        :hidden-elements="hiddenElements"
        :tip="tip"
        :total="total"
        :tds-provider="tdsProvider"
        :loading-alt-payment-methods="isConfirmingOrderTotal"
        @checkout="checkout"
      >
        <template #top>
          <slot name="payment-modal-top" />
        </template>
      </payment-information>
    </modal>

    <modal
      v-slot="{ data }"
      name="chargeToRoom"
      modal-style="compact"
    >
      <charge-to-room
        :hide-contact-info="hiddenElements.contactInfo"
        :pay-cta-text="payCtaText"
        :phone-is-required="phoneIsRequired"
        :email-input-state="emailInputState"
        :room-number-autofill="roomNumber"
        :show-total="!hiddenElements.total"
        :total="total"
        :hide-sms="data.hideSms"
        @checkout="checkout"
      />
    </modal>

    <modal
      v-slot="{ data: { onConfirm, location } }"
      name="confirmPickupLocation"
      modal-style="compact"
    >
      <confirm-location
        :location="location"
        @confirm="onConfirm"
      />
    </modal>

    <modal
      name="totalChangedWarning"
      modal-style="compact"
    >
      <div>
        <div class="section section--half-y-padding">
          <h2 class="title title--2">
            Order Total Changed
          </h2>
        </div>
        <div class="section">
          <div class="flex align-center spacing">
            <span
              class="icon icon--medium fa-dollar-sign primary"
              style="margin-right: 1.5rem"
            />
            <p class="no-spacing">
              Your order total has changed. Please re-review your order before continuing.
            </p>
          </div>
        </div>
        <div class="section section--half-y-padding section--alt-background">
          <button
            class="button button--primary button--large"
            type="button"
            @click="$closeModal('totalChangedWarning')"
          >
            Okay
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
  import ChargeToRoom from './charge-to-room.vue';
  import ConfirmLocation from './confirm-location.vue';
  import PaymentInformation from './payment-information.vue';
  import loadPaymentSDK from '@/helpers/load-payment-sdk';
  import getTdsProvider from '@/helpers/getTdsProvider';
  import { mapGetters } from 'vuex';
  import logger from '@/services/logger';

  export default {
    name: 'PaymentMethods',

    components: {
      ChargeToRoom,
      ConfirmLocation,
      PaymentInformation
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      confirmPickupLocation: {
        type: Boolean,
        default: false
      },

      disabled: {
        type: Boolean,
        default: false
      },

      forceSaveCards: {
        type: Boolean,
        default: false
      },

      hiddenElements: {
        type: Object,
        default: () => ({}),
        validator(obj) {
          const validKeys = [
            'contactInfo',
            'giftCards',
            'roomCharge',
            'signIn',
            'smsReceipt',
            'tendr',
            'total'
          ];
          const invalidKey = Object.keys(obj).find(key => !validKeys.includes(key));
          if (invalidKey) logger.error(`'${invalidKey}' is not a valid hidden element. Must be one of: [${validKeys.join(', ')}]`);
          return !invalidKey;
        }
      },

      payCtaText: {
        type: String,
        default: 'Pay'
      },

      paymentInfoOpen: {
        type: Boolean,
        default: false
      },

      paymentGatewayOverride: {
        type: Object,
        default: null
      },

      paymentModalTitle: {
        type: String,
        default: null
      },

      paymentTotalTitle: {
        type: String,
        default: undefined
      },

      giftCardReloadPaymentMethod: {
        type: Object,
        default: null
      },

      primaryCtaText: {
        type: String,
        default: null
      },

      roomNumber: {
        type: String,
        default: null
      },

      reconfirmOrderTotal: {
        type: Boolean,
        default: false
      },

      isPreAuth: {
        type: Boolean,
        default: false
      },

      tip: {
        type: Number,
        default: 0
      },

      total: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        isLoadingLibraries: false,
        isConfirmingOrderTotal: false,
        tdsProvider: {}
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['orderDetails', 'session']),
      ...mapGetters('socket', ['orderRoom']),

      paymentGateway() {
        return this.paymentGatewayOverride || this.selectedLocation.paymentGateway;
      },

      emailInputState() {
        const { emailHiddenOnCheckout, emailOptionalOnCheckout } = this.merchant.options;
        if (emailHiddenOnCheckout) return 'hidden';
        if (emailOptionalOnCheckout) return 'optional';
        return 'required';
      },

      phoneIsRequired() {
        const { fulfillmentType } = this.orderDetails;
        return fulfillmentType === 'delivery'
          || fulfillmentType === 'shipping'
          || (this.merchant.options.phoneRequiredOnCheckout && fulfillmentType !== 'dine-in');
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      async createdHandler() {
        this.isLoadingLibraries = true;

        await loadPaymentSDK(this.paymentGateway);

        this.tdsProvider = getTdsProvider(this.paymentGateway.providers);

        this.isLoadingLibraries = false;

        if (this.paymentInfoOpen) this.$openModal('paymentInformation');
      },

      checkout(info) {
        if (info.contactInfo.phoneNumber) {
          info.contactInfo = {
            ...info.contactInfo,
            phoneNumber: info.contactInfo.phoneNumber.replace(/[^0-9]/g, '').substr(-10)
          };
        }

        this.$emit('checkout', info);
      },

      close() {
        this.$closeModal('addGiftCard');
        this.$closeModal('chargeToRoom');
        this.$closeModal('missingContactInformation');
        this.$closeModal('paymentInformation');
      },

      async openModalHandler(modalName) {
        const { enabled, dollarTriggerAmount, triggerBeforeViewTicket } = this.merchant.payAtTable2FA;

        const requiresAuthBeforePayment = enabled && !triggerBeforeViewTicket;

        const meetsCheckoutTotalThreshold = this.orderRoom.you.total >= dollarTriggerAmount;

        const formIsValid = await this.validatedForm.validate();

        if (formIsValid) {
          if (this.confirmPickupLocation) {
            this.$openModal('confirmPickupLocation', {
              location: this.selectedLocation,
              onConfirm: () => {
                this.$openModal(modalName, {}, {
                  closeModal: 'confirmPickupLocation'
                });
              }
            });
          }
          else if (requiresAuthBeforePayment && meetsCheckoutTotalThreshold && !this.session.isSignedIn) {
            const passcodeModalPayload = {
              onSuccess: () => {
                this.$openModal(modalName);
              }
            };

            this.$openModal('requestTwoFactorAuthPasscodeModal', passcodeModalPayload);
          }
          else {
            this.$openModal(modalName);
          }
        }

        if (this.reconfirmOrderTotal) {
          try {
            this.isConfirmingOrderTotal = true;
            await this.$store.dispatch('orders/getOrderAtTable', {
              silent: true,
              confirmTotalBeforePayment: true
            });
          }
          finally {
            this.isConfirmingOrderTotal = false;
          }
        }
      }
    }
  };
</script>
